export const SITE_MODES = {
  agency: "agency",
  portfolio: "portfolio",
};

// Portfolio is the default: the agency site is opt-in via REACT_APP_SITE_MODE.
export const DEFAULT_SITE_MODE = SITE_MODES.portfolio;

export const SITE_MODE_STORAGE_KEY = "interware-site-mode";
export const SITE_MODE_QUERY_PARAM = "mode";

// Which landing sections each mode renders. `agency` is the full commercial
// site; `portfolio` is a plain showcase with no services, track record,
// philosophy or discovery funnel.
const MODE_FEATURES = {
  [SITE_MODES.agency]: {
    services: true,
    experience: true,
    portfolio: true,
    philosophy: true,
    discovery: true,
  },
  [SITE_MODES.portfolio]: {
    services: false,
    experience: false,
    portfolio: true,
    philosophy: false,
    discovery: false,
  },
};

const normalizeMode = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return SITE_MODES[normalized] || "";
};

// Build-time mode. Anything unrecognized falls back to the default.
export const configuredSiteMode = normalizeMode(process.env.REACT_APP_SITE_MODE) || DEFAULT_SITE_MODE;

const readStoredOverride = () => {
  try {
    return normalizeMode(window.localStorage.getItem(SITE_MODE_STORAGE_KEY));
  } catch (_error) {
    return "";
  }
};

const writeStoredOverride = (mode) => {
  try {
    if (mode) {
      window.localStorage.setItem(SITE_MODE_STORAGE_KEY, mode);
    } else {
      window.localStorage.removeItem(SITE_MODE_STORAGE_KEY);
    }
  } catch (_error) {
    // Storage can be unavailable (private mode, blocked cookies); the URL
    // param still applies for the current page load.
  }
};

const readQueryOverride = () => {
  try {
    const raw = new URLSearchParams(window.location.search).get(SITE_MODE_QUERY_PARAM);
    if (raw === null) {
      return null;
    }
    // `?mode=` or `?mode=default` clears a previously persisted override.
    return normalizeMode(raw);
  } catch (_error) {
    return null;
  }
};

// Resolution order: URL param (persisted) -> stored override -> build-time env.
export const resolveSiteMode = () => {
  if (typeof window === "undefined") {
    return configuredSiteMode;
  }

  const fromQuery = readQueryOverride();
  if (fromQuery !== null) {
    writeStoredOverride(fromQuery);
    return fromQuery || configuredSiteMode;
  }

  return readStoredOverride() || configuredSiteMode;
};

export const setSiteModeOverride = (mode) => {
  const normalized = normalizeMode(mode);
  writeStoredOverride(normalized);
  return normalized || configuredSiteMode;
};

export const clearSiteModeOverride = () => setSiteModeOverride("");

export const getSiteModeFeatures = (mode) =>
  MODE_FEATURES[mode] || MODE_FEATURES[DEFAULT_SITE_MODE];
